import React, { useLayoutEffect, useState } from "react"
import {
    ActionIcon,
    CopyButton,
    createStyles,
    Flex,
    SegmentedControl,
    Spoiler,
    Stack,
    Tooltip,
    useMantineTheme,
    Text,
} from "@mantine/core"
import Markdown from "react-markdown"

import { Icon } from "quorumdesign"
import { PersonalizedSummary } from "@/components/PersonalizedSummary/PersonalizedSummary"

const useStyles = createStyles((theme) => ({
    alert: {
        borderRadius: "8px",
        border: `1px solid ${theme.colors.violet[3]}`,
        backgroundColor: theme.colors.violet[0],
        padding: "16px",
        alignSelf: "stretch",
    },
}))

const SummarySingleSentenceAlert = ({ children }) => {
    const { classes } = useStyles()
    return <div className={classes.alert}>{children}</div>
}

export const SummarySectionWithPersonalizedTab = ({
    aiSummary,
    aiSummaryDate,
    aiSummarySingleSentence,
    profileId,
    billId,
    boardId,
    officialSummary,
}) => {
    const theme = useMantineTheme()
    const [value, setValue] = useState("")
    const [personalizedSummary, setPersonalizedSummary] = useState(null)

    // Component will initially render without any summary, so we useEffect to check if it's available on later renders.
    useLayoutEffect(() => {
        // Always default to personalized tab first, allowing users to access it regardless of other summaries
        if (!value) {
            setValue("personalized")
        }
    }, [aiSummary, officialSummary, value])

    // Build the segmented control data - personalized tab first, then other summaries
    const segmentedControlData = [
        {
            label: (
                <Flex gap="0.5rem" align="center" sx={{ color: theme.colors.violet[9] }}>
                    <Icon icon="user" iconFamily="fas" size="sm" color={theme.colors.violet[9]} />
                    AI Personalized
                </Flex>
            ),
            value: "personalized",
        },
        ...(aiSummary
            ? [
                  {
                      label: (
                          <Flex gap="0.5rem" align="center" sx={{ color: theme.colors.violet[9] }}>
                              <Icon icon="sparkles" iconFamily="fas" size="sm" color={theme.colors.violet[9]} />
                              AI Summary
                          </Flex>
                      ),
                      value: "ai",
                  },
              ]
            : []),
        ...(officialSummary ? [{ label: "Official Summary", value: "official" }] : []),
    ]

    // Always show segmented control since personalized tab is always available
    const showSegmentedControl = true

    return (
        <Stack spacing="xs">
            {showSegmentedControl && (
                <SegmentedControl
                    data={segmentedControlData}
                    value={value}
                    onChange={setValue}
                    size="xs"
                    // Isolate this component to avoid rendering on top of the NavigationTabs
                    style={{ isolation: "isolate" }}
                />
            )}

            {value === "ai" && aiSummarySingleSentence && (
                <SummarySingleSentenceAlert>
                    <strong>Key Takeaway: </strong>
                    {aiSummarySingleSentence}
                </SummarySingleSentenceAlert>
            )}

            {value === "personalized" && (
                <PersonalizedSummary profileId={profileId} billId={billId} boardId={boardId} />
            )}

            {value !== "personalized" && (
                <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                    <Stack spacing="xs">
                        {value === "ai" ? <Markdown>{aiSummary}</Markdown> : <span>{officialSummary}</span>}
                        {value === "ai" && aiSummaryDate && (
                            <Text size="sm" color="dimmed">
                                Updated: {aiSummaryDate}
                            </Text>
                        )}
                    </Stack>
                </Spoiler>
            )}

            <CopyButton
                value={
                    value === "personalized" && personalizedSummary
                        ? personalizedSummary.summary
                        : value === "ai"
                          ? aiSummary
                          : officialSummary
                }
                timeout={2000}
            >
                {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="right">
                        <ActionIcon
                            color={copied ? "teal" : "gray"}
                            onClick={copy}
                            aria-label="Copy this message"
                            data-plum-id={
                                value === "personalized"
                                    ? "personalized-summary-copy-button"
                                    : value === "ai"
                                      ? "ai-summary-copy-button"
                                      : "official-summary-copy-button"
                            }
                        >
                            {copied ? (
                                <Icon icon="check" size="sm" />
                            ) : (
                                <Icon icon="copy" size="sm" color={theme.colors.gray[7]} />
                            )}
                        </ActionIcon>
                    </Tooltip>
                )}
            </CopyButton>
        </Stack>
    )
}
